import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, take, takeUntil, tap } from 'rxjs/operators';

interface MyObject {
  _id: number;
  label: string;
  selected?: boolean;
}

@Component({
  selector: 'form-multi-select-search',
  templateUrl: './form-search-multiple.component.html',
  styleUrls: ['./form-search-multiple.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormMultiSelectSearchComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() preSelectedElement: Array<any>;

  @Input() formGroup!: UntypedFormGroup;
  @Input() controlName!: string;
  @Input() label = 'Recherche et sélectionnez...';
  @Input() appearance: 'outline' | 'fill' = 'outline';
  @Input() displayProperty = '';
  @Input() withoutPadding = false;
  @Input() placeholder = 'Rechercher...';
  @Input() displayFn!: (data: any) => string;
  @Input() filteredStates$: Observable<MyObject[]>;
  @Input() defaultErrorMessage: any;
  @Input() invalidPatternMessage = 'Le modèle est invalide';
  @Input() customErrorMessage = '';
  @Input() selectedItems: Array<any> = [];
  @Input() isStaticData = false;
  /** Opt-in : affiche la sélection en chips (avec wrap) DANS le champ, au lieu du texte
   * « N sélectionné(s) ». Défaut false = affichage historique, aucun impact sur l'existant. */
  @Input() chipsInField = false;
  @Input() initializedData: any = null;

  @Output() onSelectItems = new EventEmitter();
  @Output() onScroll = new EventEmitter();
  @Output() autocompleteChange = new EventEmitter<string>();

  isLoading = false;
  hasData = true;
  private isScrollPending = false;

  searchControl = new UntypedFormControl('');
  selectAllChecked = false;

  items: MyObject[] = [];
  filteredItems: MyObject[] = [];
  formGroupSubscription!: Subscription;
  private destroy$ = new Subject<void>();

  @ViewChild('trigger', { read: ElementRef }) trigger: ElementRef;
  @ViewChild('searchInput', { read: ElementRef }) searchInput: ElementRef;
  menuWidth: number;

  constructor(private cdr: ChangeDetectorRef) {}

  /** Contrôle désactivé : ni menu (recherche, cases, tout sélectionner), ni retrait de chip, ni vidage. */
  get isDisabled(): boolean {
    return !!this.formGroup?.controls[this.controlName]?.disabled;
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    this.adjustMenuWidth();
  }

  adjustMenuWidth(): void {
    if (this.trigger) {
      this.menuWidth = this.trigger.nativeElement.offsetWidth - 5;
    }
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.preSelectedElement && this.preSelectedElement) {
      this.processPreSelectedItems();
    }

    // Si filteredStates$ change et qu'on est en mode statique, réinitialiser les items
    if (changes.filteredStates$ && this.isStaticData && this.filteredStates$) {
      this.filteredStates$.pipe(take(1)).subscribe(items => {
        this.initializeStaticItems(items);
      });
    }
  }

  ngOnInit() {
    let lastSearchTerm = '';
    let isFirstLoad = true;

    // Charger les données initiales pour les données non-statiques
    if (!this.isStaticData) {
      this.isLoading = true;
      this.autocompleteChange.emit('');
    }

    if (this.isStaticData) {
      // S'abonner à filteredStates$ seulement s'il existe
      if (this.filteredStates$) {
        this.filteredStates$.pipe(takeUntil(this.destroy$)).subscribe(items => {
          this.initializeStaticItems(items);
        });
      }

      this.searchControl.valueChanges
        .pipe(
          startWith(''),
          map(searchTerm => {
            this.filteredItems = this.filterItem(this.getSortedItems(this.items), searchTerm);
          }),
        )
        .subscribe();
    } else {
      this.filteredStates$
        .pipe(
          takeUntil(this.destroy$),
          // Ajouter startWith pour forcer l'émission initiale
          startWith([]),
        )
        .subscribe(items => {
          this.isLoading = false;
          this.isScrollPending = false;

          const selectedMap = new Map(this.items.map(item => [item._id, item]));
          const controlIds = this.getControlIds();

          this.items = items.map(item => {
            const existingItem = selectedMap.get(item._id);

            return {
              ...item,
              ...(existingItem ?? {}),
              // Priorité : état local explicite (clic) > flag du flux (restauration) > présence dans le contrôle.
              // Pas de défaut `false` : "jamais touché" doit rester distinct de "décoché par l'utilisateur".
              selected: existingItem?.selected ?? item.selected ?? (controlIds.has(item._id) || undefined),
            };
          });

          // PRED-3139 : préserver les éléments SÉLECTIONNÉS absents de l'émission courante
          // (présélection posée avant la première émission `startWith([])`, ou item hors de la
          // page/recherche renvoyée par le serveur) — sinon la sélection disparaît de l'affichage
          // alors que le FormControl la contient toujours.
          const preservedSelected = [...selectedMap.values()].filter(
            previous => previous.selected && !this.items.some(item => item._id === previous._id),
          );
          if (preservedSelected.length) {
            this.items = [...this.items, ...preservedSelected];
          }

          this.refreshFilteredItems(lastSearchTerm);

          this.applyInitializedData();

          this.hasData = this.filteredItems.length > 0;

          // Forcer la détection de changement
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });

      this.searchControl.valueChanges
        .pipe(
          debounceTime(300),

          distinctUntilChanged(),

          tap(searchTerm => {
            lastSearchTerm = searchTerm;
            this.isLoading = true;
            this.hasData = false;

            this.autocompleteChange.emit(searchTerm);
          }),

          map(searchTerm => {
            const allItems = this.getSortedItems(this.items);
            return this.filterItem(allItems, searchTerm);
          }),

          tap(filtered => {
            this.filteredItems = filtered;
            this.hasData = filtered.length > 0;
            this.isLoading = false;
          }),
        )
        .subscribe(() => {
          this.cdr.detectChanges();
        });
    }

    // Sur SON contrôle uniquement : à l'écoute du groupe, le patch d'un contrôle voisin faisait passer
    // ce champ (encore null) pour "réinitialisé" et figeait ses items à selected=false (PRED-3054).
    this.formGroupSubscription = this.formGroup.get(this.controlName)?.valueChanges.subscribe(() => {
      this.resetSelectionsIfFormReset();
    });
    this.formGroup
      .get(this.controlName)
      ?.statusChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    if (this.formGroupSubscription) {
      this.formGroupSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // PRED-3076 : recalcule la liste affichée en réappliquant le terme de recherche courant,
  // sinon les ré-injections d'items (initializedData, données statiques) écrasent le filtre
  private refreshFilteredItems(searchTerm: string = this.searchControl.value): void {
    this.filteredItems = this.filterItem(this.getSortedItems(this.items), searchTerm);
  }

  filterItem(items: MyObject[], searchTerm: string): MyObject[] {
    if (!searchTerm) {
      return items;
    }

    const lowerSearchTerm = this.normalizeString(searchTerm);
    return items.filter(item => this.normalizeString(item[this.displayProperty]).includes(lowerSearchTerm));
  }

  protected normalizeString(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  getSelectedItemsText(): string {
    const selectedItems = this.getSortedItems(this.items).filter(item => item.selected);
    if (selectedItems.length === 0) return null;
    if (selectedItems.length === 1) return selectedItems[0][this.displayProperty];
    return `${selectedItems.length} sélectionné(s)`;
  }

  updateFormControl(emitEvent: boolean = true) {
    const selectedItems = this.items.filter(item => item.selected);
    const formControl = this.formGroup.get(this.controlName);
    if (formControl) {
      formControl.setValue(selectedItems, { emitEvent });
    }
  }

  /** PRED-3139 (opt-in, défaut = affichage existant) : items sélectionnés rendus en chips
   * DANS le champ (avec wrap), triés comme le dropdown. */
  selectedItemsForChips(): any[] {
    return this.getSortedItems(this.items).filter(item => item.selected);
  }

  removeChip(item: any, event: Event): void {
    event.stopPropagation(); // ne pas ouvrir le menu du champ
    this.updateSelectedItems(item, false);
  }

  toggleSelection(item: any, event: Event) {
    event.stopPropagation(); // Empêche la fermeture de la fenêtre
    const newCheckedState = !item.selected;
    this.updateSelectedItems(item, newCheckedState);
  }

  updateSelectedItems(item: any, checked: boolean) {
    item.selected = checked;
    this.items = this.items.map(i => (i._id === item._id ? item : i));
    this.selectAllChecked = this.items.every(i => i.selected);

    const selectedItems = this.items.filter(i => i.selected);

    // emit event to update parent
    this.onSelectItems.emit(selectedItems);

    this.updateFormControl();
  }

  toggleSelectAll(checked: boolean) {
    this.items.forEach(item => (item.selected = checked));
    this.filteredItems = this.getSortedItems(this.items);
    this.selectAllChecked = checked;
    this.updateFormControl();
  }

  clearSearch() {
    this.searchControl.setValue('');
    this.filteredItems = this.getSortedItems(this.items);
  }

  hasSelectedItems(): boolean {
    return this.items && this.items.some(item => item.selected);
  }

  isRequired(): boolean {
    const control = this.formGroup?.get(this.controlName);
    if (!control) {
      return false;
    }

    const validator = control.validator ? control.validator({} as any) : null;
    return !!(validator && validator.required);
  }

  clearAllSelections(event: Event) {
    event.stopPropagation();
    this.resetSelections();
  }

  getSortedItems(items: any[]) {
    return items
      .filter(x => !!x[this.displayProperty])
      .sort((a, b) => a[this.displayProperty].localeCompare(b[this.displayProperty]));
  }

  resetSelectionsIfFormReset() {
    const control = this.formGroup.get(this.controlName);
    // Ne reset que si la valeur est explicitement null, undefined ou chaîne vide
    // Un tableau vide [] ne doit PAS déclencher de reset (sélection en cours)
    if (control && (control.value === null || control.value === undefined || control.value === '')) {
      this.resetSelections();
    }
  }

  resetSelections() {
    this.items.forEach(item => (item.selected = false));
    this.selectAllChecked = false;
    this.clearSearch();
    this.updateFormControl();
    // Notify parent components that selection has been cleared
    try {
      this.onSelectItems.emit([]);
    } catch {}
  }

  onScrollLine(event: number) {
    if (this.isScrollPending) return;

    const threshold = 0.2;
    const totalItems = this.items.length;
    const scrollPosition = event;

    const upperThreshold = totalItems * (1 - threshold);

    if (scrollPosition > upperThreshold) {
      this.isScrollPending = true;
      this.onScroll.emit('next');
    }
  }

  onMenuOpened() {
    if (this.isDisabled) {
      return;
    }
    // Auto-focus sur le champ de recherche
    setTimeout(() => {
      if (this.searchInput && this.searchInput.nativeElement) {
        this.searchInput.nativeElement.focus();
      }
    }, 200);

    // Déclencher la recherche automatique à l'ouverture si ce n'est pas des données statiques
    if (!this.isStaticData) {
      // Déclencher immédiatement la recherche
      this.autocompleteChange.emit('');
    }
  }

  onItemClick(item: any, event: Event) {
    event.stopPropagation();
    this.updateSelectedItems(item, !item.selected);
  }

  private applyInitializedData() {
    if (!this.initializedData) {
      return;
    }

    const initializedIds = Array.isArray(this.initializedData)
      ? this.initializedData.map(i => i._id)
      : [this.initializedData._id];

    initializedIds.forEach(initId => {
      const alreadyPresent = this.items.some(item => item._id === initId);
      if (!alreadyPresent) {
        const foundInit = Array.isArray(this.initializedData)
          ? this.initializedData.find(i => i._id === initId)
          : this.initializedData;

        if (foundInit) {
          this.items.push({ ...foundInit, selected: true });
        }
      } else {
        this.items = this.items.map(item => (item._id === initId ? { ...item, selected: true } : item));
      }
    });

    this.refreshFilteredItems();
    this.updateFormControl(false);
  }

  private initializeStaticItems(items: any[]): void {
    // Préserver les sélections existantes dans this.items
    const currentSelections = new Map(this.items.filter(i => i.selected).map(i => [i._id, true]));
    // Valeur du contrôle = sélection restaurée (URL) : sans elle, il fallait marquer `selected` sur les objets
    // de la liste source, flag que « Effacer tout » ne pouvait plus retirer (PRED-3281).
    const controlIds = this.getControlIds();

    this.items = items.map(item => {
      const isSelected = this.selectedItems.some(selectedItem => selectedItem._id === item._id);
      const wasSelected = currentSelections.get(item._id) || false;

      return { ...item, selected: item.selected || isSelected || wasSelected || controlIds.has(item._id) };
    });

    this.applyInitializedData();

    this.refreshFilteredItems();
    this.selectAllChecked = this.items.length > 0 && this.items.every(i => i.selected);
    this.updateFormControl(false);
    this.cdr.detectChanges();
  }

  private getControlIds(): Set<unknown> {
    const controlValue = this.formGroup.get(this.controlName)?.value;
    return new Set(Array.isArray(controlValue) ? controlValue.map(v => v?._id) : []);
  }

  private processPreSelectedItems(): void {
    if (this.preSelectedElement && this.preSelectedElement.length > 0) {
      // Si ce n'est pas des données statiques, déclencher le chargement initial
      if (!this.isStaticData) {
        this.autocompleteChange.emit('');
      }

      // Ajouter les éléments pré-sélectionnés à la liste
      this.preSelectedElement.forEach(item => {
        const existingItem = this.items.find(i => i._id === item._id);

        if (!existingItem) {
          // Ajouter l'élément s'il n'existe pas dans la liste
          this.items.push({ ...item, selected: true });
        } else {
          // Marquer l'élément existant comme sélectionné
          existingItem.selected = true;
        }
      });

      // Mettre à jour les éléments filtrés
      this.filteredItems = this.getSortedItems(this.items);
      this.selectAllChecked = this.items.length > 0 && this.items.every(i => i.selected);

      // Mettre à jour le FormControl
      this.updateFormControl();

      // Déclencher la détection de changements
      this.cdr.detectChanges();
    }
  }
}
